// src/rms/pricing/availability.js
// ============================================================================
// Availability resolution
// ============================================================================
// Mirrors the SQL function rms_asset_is_available(). Three things can make an
// asset unavailable for a window, and only the first is visible to the database
// exclusion constraint:
//
//   1. an overlapping live reservation
//   2. that reservation's TURNAROUND buffer — cleaning, drying, servicing
//   3. a blackout window — maintenance, seasonal closure, owner use
//
// Group allocation lives here too: booking a group means finding a free member,
// so a listing with five units accepts five concurrent rentals instead of
// blocking after the first.
// ============================================================================

/**
 * Statuses that hold a unit. Exported because the timeline draws occupancy from
 * the same list -- a screen with its own copy would drift the day someone adds
 * a status, and it would drift silently.
 */
export const BLOCKING_RESERVATION_STATUSES = ["pending", "confirmed", "checked_out", "active"];

const ms = (v) => (v ? new Date(v).getTime() : null);
const END_OF_TIME = Number.POSITIVE_INFINITY;

function overlaps(aStart, aEnd, bStart, bEnd) {
  // Half-open intervals: a rental ending exactly when the next begins is fine.
  return aStart < bEnd && bStart < aEnd;
}

/**
 * The `where` clause that loads exactly the reservations touching a window.
 *
 * Overlap is the same half-open test used throughout this file, expressed as a
 * query instead of a predicate: a booking touches [start, end) when it begins
 * before the window ends and finishes after the window begins.
 *
 * Two details that are easy to get wrong and expensive to get wrong quietly:
 *
 * `orNull` on the end. An open-ended booking -- every storage occupancy, and
 * anything still running in a continuous vertical -- has ends_at NULL, and NULL
 * loses every comparison. Omit this and the query drops precisely the bookings
 * that are still live.
 *
 * `paddingMinutes`. Turnaround pads an existing booking on both sides, so a
 * booking that ENDS just before the window can still block its first hour. The
 * caller passes the largest turnaround in the fleet; loading a few extra rows
 * is cheaper than an availability answer that is wrong at the edges.
 */
export function overlapWindow({ startsAt, endsAt, paddingMinutes = 0 }) {
  const pad = paddingMinutes * 60000;
  const from = new Date(ms(startsAt) - pad).toISOString();
  const to = endsAt ? new Date(ms(endsAt) + pad).toISOString() : null;

  const where = { ends_at: { gt: from, orNull: true } };
  // An open-ended window has no upper bound to filter on -- everything from
  // `from` onwards touches it.
  if (to) where.starts_at = { lt: to };
  return where;
}

/**
 * Reservations bucketed by the asset they are on.
 *
 * The availability checks below take a `reservations` array and scan it once
 * per asset. That is fine for a handful of units and quadratic for a fleet:
 * fifty units against a year of history is fifty full scans per keystroke.
 * Bucket once, hand each check its own slice.
 *
 * Pool bookings with no asset_id yet are collected under `unassigned` rather
 * than dropped -- they are real commitments against the group even before a
 * physical unit is chosen at checkout.
 */
export function byAsset(reservations = []) {
  const map = new Map();
  for (const r of reservations) {
    const key = r.asset_id || "unassigned";
    const bucket = map.get(key);
    if (bucket) bucket.push(r);
    else map.set(key, [r]);
  }
  return map;
}

/** The slice for one asset, or an empty array. Never undefined. */
export function reservationsFor(map, assetId) {
  return map.get(assetId) || [];
}

/**
 * Why an asset can't be booked for a window, or null if it can.
 * Returns a reason object so the UI can say "blocked: annual service" rather
 * than "unavailable".
 */
export function availabilityConflict({
  asset,
  reservations = [],
  blocks = [],
  startsAt,
  endsAt,
  excludeReservationId = null,
  staffBooking = true,
}) {
  const start = ms(startsAt);
  const end = ms(endsAt) ?? END_OF_TIME;

  if (asset.status === "maintenance" || asset.status === "out_of_service" || asset.status === "retired") {
    return { kind: "status", message: `${asset.name} is ${asset.status.replace(/_/g, " ")}.` };
  }

  const buffer = (asset.turnaround_minutes || 0) * 60000;

  const clash = reservations.find((r) => {
    if (r.asset_id !== asset.id) return false;
    if (r.id === excludeReservationId) return false;
    if (!BLOCKING_RESERVATION_STATUSES.includes(r.status)) return false;

    // Pad the existing booking on both sides by the turnaround buffer.
    const rStart = ms(r.starts_at) - buffer;
    const rEnd = (ms(r.ends_at) ?? END_OF_TIME) + buffer;
    return overlaps(rStart, rEnd, start, end);
  });

  if (clash) {
    const clashStart = ms(clash.starts_at);
    const clashEnd = ms(clash.ends_at) ?? END_OF_TIME;
    // Distinguish a genuine double-booking from a turnaround collision — the
    // second is fixable by moving the pickup a few hours, and staff should be
    // told which it is.
    const isBufferOnly = !overlaps(clashStart, clashEnd, start, end);
    return {
      kind: isBufferOnly ? "turnaround" : "reservation",
      reservation: clash,
      message: isBufferOnly
        ? `${asset.name} needs ${formatMinutes(asset.turnaround_minutes)} turnaround around ${clash.reservation_number}.`
        : `${asset.name} is already booked on ${clash.reservation_number} for that window.`,
    };
  }

  const block = blocks.find((b) => {
    const targetsAsset = b.asset_id === asset.id;
    const targetsGroup = asset.group_id && b.asset_group_id === asset.group_id;
    if (!targetsAsset && !targetsGroup) return false;
    if (staffBooking ? !b.blocks_staff_booking : !b.blocks_customer_booking) return false;
    return overlaps(ms(b.starts_at), ms(b.ends_at), start, end);
  });

  if (block) {
    return {
      kind: "blackout",
      block,
      message: `${asset.name} is blocked for that window${block.reason ? ` — ${block.reason}` : ""}.`,
    };
  }

  return null;
}

/**
 * Does this blackout apply to this asset? Same predicate the conflict check
 * uses, minus the date test, so a caller drawing blocks on a grid targets
 * exactly what the engine would refuse a booking for.
 */
export function blockApplies(block, asset, staffBooking = true) {
  const targetsAsset = block.asset_id === asset.id;
  const targetsGroup = asset.group_id && block.asset_group_id === asset.group_id;
  if (!targetsAsset && !targetsGroup) return false;
  return staffBooking ? !!block.blocks_staff_booking : !!block.blocks_customer_booking;
}

export function isAvailable(args) {
  return availabilityConflict(args) === null;
}

/**
 * Picks a free member of a group for a window.
 *
 * Prefers the least-recently-used unit so wear spreads evenly across a fleet
 * rather than always going out on unit #1 — a small thing that matters a lot to
 * anyone running identical machines.
 */
export function allocateFromGroup({
  groupAssets, reservations, blocks, startsAt, endsAt, staffBooking = true,
  reservationsByAsset = null,
}) {
  const forAsset = (a) =>
    reservationsByAsset ? reservationsFor(reservationsByAsset, a.id) : reservations;

  const candidates = groupAssets
    .filter((a) => a.is_active !== false)
    .filter((a) => isAvailable({ asset: a, reservations: forAsset(a), blocks, startsAt, endsAt, staffBooking }));

  if (!candidates.length) return { asset: null, conflict: firstConflict({ groupAssets, reservations, blocks, startsAt, endsAt, staffBooking }) };

  const lastUsed = {};
  reservations.forEach((r) => {
    const t = ms(r.starts_at);
    if (!lastUsed[r.asset_id] || t > lastUsed[r.asset_id]) lastUsed[r.asset_id] = t;
  });

  const chosen = candidates
    .slice()
    .sort((a, b) => (lastUsed[a.id] || 0) - (lastUsed[b.id] || 0))[0];

  return { asset: chosen, conflict: null };
}

function firstConflict({ groupAssets, reservations, blocks, startsAt, endsAt, staffBooking }) {
  for (const a of groupAssets) {
    const c = availabilityConflict({ asset: a, reservations, blocks, startsAt, endsAt, staffBooking });
    if (c) return c;
  }
  return { kind: "none_free", message: "No units in this group are free for that window." };
}

/** How many members of a group are free — drives "3 of 5 available". */
export function groupAvailability({
  groupAssets, reservations, blocks, startsAt, endsAt, staffBooking = true,
  reservationsByAsset = null,
}) {
  // With a bucket map the per-unit check sees only that unit's bookings; the
  // plain array still works for callers that have not bucketed.
  const forAsset = (a) =>
    reservationsByAsset ? reservationsFor(reservationsByAsset, a.id) : reservations;

  const free = groupAssets.filter((a) =>
    isAvailable({ asset: a, reservations: forAsset(a), blocks, startsAt, endsAt, staffBooking })
  );
  return { total: groupAssets.length, free: free.length, assets: free };
}

/**
 * Fleet utilisation for a window, as a percentage. Feeds utilisation-based rate
 * rules ("surge 15% when over 80% of the fleet is out").
 */
export function utilizationPct({ assets, reservations, startsAt, endsAt, reservationsByAsset = null }) {
  const bookable = assets.filter((a) => a.is_active !== false && a.status !== "retired");
  if (!bookable.length) return 0;

  const start = ms(startsAt);
  const end = ms(endsAt) ?? END_OF_TIME;

  const forAsset = (a) =>
    reservationsByAsset ? reservationsFor(reservationsByAsset, a.id) : reservations;

  const busy = bookable.filter((a) =>
    forAsset(a).some(
      (r) =>
        r.asset_id === a.id &&
        BLOCKING_RESERVATION_STATUSES.includes(r.status) &&
        overlaps(ms(r.starts_at), ms(r.ends_at) ?? END_OF_TIME, start, end)
    )
  );

  return Math.round((busy.length / bookable.length) * 100);
}

export function formatMinutes(minutes) {
  const m = Number(minutes) || 0;
  if (m === 0) return "no";
  if (m < 60) return `${m} min`;
  if (m < 1440) {
    const h = m / 60;
    return `${Number.isInteger(h) ? h : h.toFixed(1)} hr`;
  }
  const d = m / 1440;
  return `${Number.isInteger(d) ? d : d.toFixed(1)} day${d === 1 ? "" : "s"}`;
}

const availability = {
  BLOCKING_RESERVATION_STATUSES,
  blockApplies,
  overlapWindow,
  byAsset,
  reservationsFor,
  availabilityConflict,
  isAvailable,
  allocateFromGroup,
  groupAvailability,
  utilizationPct,
  formatMinutes,
};

export default availability;
